<template>
    <div :class="wrapperClass" v-if="post && post.status">
        <BmvDropdown align="left" :min-width="210" :stretch="!isCompact">
            <template #trigger="{ open }">
            <button
                type="button"
                v-if="variant === 'badge'"
                :aria-expanded="open ? 'true' : 'false'"
                class="bes-badge"
                :class="[`bes-badge--${statusTone}`, { 'bes-badge--loading': isLoadingStatus }]"
                :title="post.status.name"
            >
                <i :class="[post.status.icon, 'bes-badge-icon']"></i>
                <span class="bes-badge-label">{{ post.status.name }}</span>
                <i v-if="isLoadingStatus" class="fas fa-circle-notch fa-spin bes-badge-spin"></i>
            </button>

            <button
                type="button"
                v-else-if="variant === 'v2'"
                :aria-expanded="open ? 'true' : 'false'"
                class="bes-v2-btn"
                :class="{ 'bes-v2-btn--loading': isLoadingStatus }"
                :title="post.status.name"
            >
                <i :class="[post.status.icon, 'bes-v2-btn-icon']"></i>
                <span class="bes-v2-btn-label">{{ post.status.name }}</span>
                <i v-if="isLoadingStatus" class="fas fa-circle-notch fa-spin bes-v2-btn-spin"></i>
                <i v-else class="fal fa-chevron-down bes-v2-btn-caret"></i>
            </button>

            <button
                type="button"
                v-else
                :aria-expanded="open ? 'true' : 'false'"
                class="flex items-center justify-content-between px-3 border border-transparent shadow-sm text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 w-full h-full"
            >
                <i :class="post.status.icon" class="pr-2 py-2 color-white"></i>
                {{ post.status.name }}
                <i
                    v-if="isLoadingStatus === true"
                    class="fas fa-circle-notch fa-spin ml-2 text-white"
                ></i>
                <svg
                    class="ml-2 w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M19 9l-7 7-7-7"
                    ></path>
                </svg>
            </button>
            </template>

            <div :class="{ 'bes-menu--loading': isLoadingStatus }">
                <a
                    v-for="(status, statusIndex) in formStatuses"
                    :key="status.id"
                    class="dropdown-item d-flex w-100 justify-center pr-4"
                    @click.prevent="postStatus(statusIndex)"
                >
                    <span class="mr-auto">{{ status.name }}</span>
                    <span
                        v-if="status.id === post.status.id"
                        class="ml-auto status status-success mt-1.5"
                    ></span>
                </a>
            </div>
        </BmvDropdown>

        <PostStatusTransition
            v-model="transitionOpen"
            :post="post"
            :status="transitionStatus"
            :form-id="post.form ? post.form.id : null"
            @done="onTransitionDone"
            @cancel="transitionStatus = null"
        />
    </div>
</template>
<style scoped>
.bes-menu--loading {
    opacity: 0.6;
    pointer-events: none;
}

.bes-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 22px;
    padding: 0 10px;
    border-radius: 999px;
    border: 1px solid transparent;
    background: transparent;
    font-size: 0.72rem;
    font-weight: 500;
    line-height: 1;
    letter-spacing: 0.01em;
    cursor: pointer;
    white-space: nowrap;
    transition: background 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease;
    box-sizing: border-box;
    -webkit-appearance: none;
    appearance: none;
    outline: none;
}

.bes-badge:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px rgba(8, 145, 178, 0.25);
}

.bes-badge-icon {
    font-size: 0.7rem;
}

.bes-badge-label {
    letter-spacing: 0.01em;
}

.bes-badge-spin {
    font-size: 0.7rem;
    margin-left: 2px;
}

.bes-badge--success {
    background: rgba(16, 185, 129, 0.12);
    color: #047857;
}

.bes-badge--success:hover {
    background: rgba(16, 185, 129, 0.2);
}

.bes-badge--warning {
    background: rgba(245, 158, 11, 0.14);
    color: #b45309;
}

.bes-badge--warning:hover {
    background: rgba(245, 158, 11, 0.22);
}

.bes-badge--primary {
    background: rgba(8, 145, 178, 0.12);
    color: #0e7490;
}

.bes-badge--primary:hover {
    background: rgba(8, 145, 178, 0.2);
}

.bes-badge--neutral {
    background: #f1f5f9;
    color: #475569;
}

.bes-badge--neutral:hover {
    background: #e2e8f0;
}

.bes-badge--loading {
    opacity: 0.7;
    pointer-events: none;
}

.bes-v2-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 32px;
    padding: 0 12px;
    border: 1px solid var(--v2-primary);
    border-radius: var(--v2-radius-sm);
    background: var(--v2-primary);
    color: var(--v2-primary-contrast, #fff);
    font: inherit;
    font-size: 0.82rem;
    font-weight: var(--v2-font-weight-medium);
    line-height: 1.2;
    white-space: nowrap;
    cursor: pointer;
    transition: background 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;
    box-sizing: border-box;
    -webkit-appearance: none;
    appearance: none;
    outline: none;
}

.bes-v2-btn:hover:not(:disabled) {
    background: var(--v2-primary-hover);
    border-color: var(--v2-primary-hover);
}

.bes-v2-btn:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(8, 145, 178, 0.25);
}

.bes-v2-btn-icon {
    font-size: 0.8rem;
    color: inherit !important;
}

.bes-v2-btn-caret {
    font-size: 0.7rem;
    margin-left: 2px;
    color: inherit !important;
    opacity: 0.85;
}

.bes-v2-btn-spin {
    font-size: 0.8rem;
    color: inherit !important;
}

.bes-v2-btn--loading {
    opacity: 0.75;
    pointer-events: none;
}

@media (max-width: 900px) {
    .bes-v2-btn {
        padding: 0 10px;
    }
}
</style>
<script>
/**
 * SPDX-FileCopyrightText: © 2021-2026 SAS Cœur du Web, Paris (France) — https://coeurduweb.com
 * SPDX-License-Identifier: LicenseRef-CoeurDuWeb-Proprietary
 *
 * Tous droits réservés. Logiciel propriétaire, non libre.
 * Les conditions d'utilisation, le périmètre de la licence concédée et les
 * restrictions applicables figurent dans le fichier LICENCE.md à la racine du dépôt.
 */
import { mapState, mapActions } from 'vuex'
import BmvDropdown from '@/components/UI/v2/BmvDropdown.vue'
import PostStatusTransition from '@/components/Model/Post/PostStatusTransition.vue'
import { requiresTransitionModal } from '@/helpers/statusReasons'

export default {
    name: 'ButtonEditStatus',
    components: { BmvDropdown, PostStatusTransition },
    props: ['post','variant'],
    data: function () {
        return {
            isLoadingStatus: false,
            transitionOpen: false,
            transitionStatus: null
        }
    },
    computed: {
        ...mapState('form', {
            allForms: (state) => state.forms
        }),
        postForm() {
            if (!this.allForms || !this.post || !this.post.form) return null
            return this.allForms.find((f) => f.id === this.post.form.id) || null
        },
        formStatuses() {
            const form = this.postForm
            if (!form || !form.statuses) return []
            return form.statuses.filter((s) => s.active)
        },
        isCompact() {
            // Pastille et bouton v2 se dimensionnent sur leur contenu.
            return this.variant === 'badge' || this.variant === 'v2'
        },
        wrapperClass() {
            const isV2 = this.variant === 'badge' || this.variant === 'v2'
            return {
                'dropdown': true,
                'h-full': !isV2,
                'lg:w-1/4': this.variant === 'minimal',
                'bes-wrap--badge': this.variant === 'badge',
                'bes-wrap--v2': this.variant === 'v2'
            }
        },
        statusTone() {
            if (!this.post || !this.post.status) return 'neutral'
            if (this.post.status.final) return 'success'
            if (this.post.status.default) return 'warning'
            return 'primary'
        }
    },
    created() {
        if (!this.allForms) {
            this.getForms()
        }
    },
    methods: {
        ...mapActions('post', ['updateStatus', 'getPostHistories']),
        ...mapActions('form', ['getForms']),

        postStatus(statusIndex) {
            let status = this.formStatuses[statusIndex]
            if (
                this.isLoadingStatus === true ||
                parseInt(this.post.form_status_id) === parseInt(status.id)
            ) {
                return
            }

            // A status that demands a reason — or any status of a contribution
            // form — goes through the transition modal, which carries the
            // reason and the estimated delivery date.
            if (requiresTransitionModal(status, this.postForm)) {
                this.transitionStatus = status
                this.transitionOpen = true
                return
            }

            var updatedStatus = {
                id: this.post.id,
                status_id: status.id,
                form_status_id: status.id
            }

            this.isLoadingStatus = true

            this.updateStatus(updatedStatus)
                .then(() => {
                    this.applyStatus(status)
                })
                .catch((e) => {
                    this.$store.dispatch('dispatchError', e)
                })
                .finally(() => {
                    this.isLoadingStatus = false
                })
        },
        onTransitionDone({ status, payload }) {
            this.applyStatus(status)
            if (Object.prototype.hasOwnProperty.call(payload, 'estimated_delivery')) {
                this.$set(this.post, 'estimated_delivery', payload.estimated_delivery)
            }
            this.transitionStatus = null
        },
        applyStatus(status) {
            this.getPostHistories(this.post.id)
            this.post.form_status_id = status.id
            this.post.status = status
        }
    }
}
</script>


<!-- En mode kanban
s'il n'y a qu'une form, afficher automatiquement -->
<!-- s'il y a <1, forcer la selection de formulaire d'abord -->
